/** Own connection, per-module scope, and sync state for the Kuali editor. */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import type {
  ConnectorCredentialSource,
  ConnectorFeedFor,
  ConnectorSchedule,
} from '../../types'
import {
  createKualiResearchFeed,
  deleteKualiResearchConnection,
  getKualiResearchConnection,
  saveKualiResearchConnection,
  updateKualiResearchFeed,
} from '../services/api'
import type {
  KualiResearchConnectionStatus,
  KualiResearchFeedOptions,
  KualiResearchModuleOptions,
  KualiResearchSourceType,
} from '../types'
import {
  KUALI_RESEARCH_DEFAULT_MODULE_LIMIT,
  KUALI_RESEARCH_SANDBOX_API_URL,
} from '../lib/kuali-research'
import { ALL_KUALI_RESEARCH_ATTACHMENT_TYPE_CODES } from '../components/KualiResearchAttachmentTypeSelection'
import { KUALI_RESEARCH_SOURCES } from '../components/KualiResearchSourceSelection'
import { kualiResearchFormError } from '../lib/form-validation'
import {
  defaultKualiSourceDisplayFieldKeys,
  kualiSourceDisplayFields,
} from '../lib/source-display-fields'

function reconcileDisplayFieldKeys(
  selected: string[],
  previous: KualiResearchModuleOptions[],
  next: KualiResearchModuleOptions[],
): string[] {
  const previousKeys = new Set(kualiSourceDisplayFields(previous).map(field => field.key))
  const nextFields = kualiSourceDisplayFields(next)
  const nextKeys = new Set(nextFields.map(field => field.key))
  const preserved = selected.filter(key => nextKeys.has(key))
  const preservedKeys = new Set(preserved)
  const newDefaults = nextFields
    .filter(field => (
      field.default
      && !previousKeys.has(field.key)
      && !preservedKeys.has(field.key)
    ))
    .map(field => field.key)
  return [...preserved, ...newDefaults]
}

function newModule(source: KualiResearchSourceType): KualiResearchModuleOptions {
  const scope = source === 'awards'
    ? 'all'
    : 'related-selected-awards'
  return {
    source,
    // cm-coding: Shared Kuali tenants make an unbounded Subaward or
    // Negotiation preview both slow and unsafe to infer from API visibility.
    // New modules therefore wait for an explicit module ID or UMD award list;
    // users may still opt into the campus-verified Award population.
    scope,
    limit: KUALI_RESEARCH_DEFAULT_MODULE_LIMIT,
    identifiers: [],
    filters: [],
    startAfter: null,
    attachmentTypeCodes: source === 'awards'
      ? [...ALL_KUALI_RESEARCH_ATTACHMENT_TYPE_CODES]
      : [],
    includeRestrictedAttachments: false,
    includeMetadata: false,
  }
}

export function useKualiResearchFeedForm({
  api,
  orgId,
  vaultId,
  feed,
  open,
  canEditQuery,
  canAdministerWorkspace,
  onConfigured,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  feed?: ConnectorFeedFor<'kuali-research'> | null
  open: boolean
  canEditQuery: boolean
  canAdministerWorkspace: boolean
  onConfigured: () => Promise<void>
}) {
  const [connection, setConnection] = useState<KualiResearchConnectionStatus>({
    workspaceConfigured: null,
    umdConfigured: null,
    umdCredentialKeys: [],
  })
  const [credentialSource, setCredentialSource] = useState<ConnectorCredentialSource>('umd')
  const [loadingConnection, setLoadingConnection] = useState(false)
  const [saving, setSaving] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [apiUrl, setApiUrl] = useState(KUALI_RESEARCH_SANDBOX_API_URL)
  const [modules, setModules] = useState<KualiResearchModuleOptions[]>([newModule('awards')])
  const [sourceDisplayFieldKeys, setSourceDisplayFieldKeys] = useState<string[]>(
    defaultKualiSourceDisplayFieldKeys([newModule('awards')]),
  )
  const [schedule, setSchedule] = useState<ConnectorSchedule>('manual')
  const editing = Boolean(feed)

  useEffect(() => {
    if (!open) return
    const saved = feed?.kualiResearch
    setCredentialSource(saved?.credentialSource ?? 'umd')
    setApiKey('')
    setApiUrl(saved?.apiUrl ?? KUALI_RESEARCH_SANDBOX_API_URL)
    const savedModules = saved?.modules
      ? saved.modules.map(module => ({ ...module }))
      : [newModule('awards')]
    setModules(savedModules)
    setSourceDisplayFieldKeys(
      feed
        ? feed.sourceDisplayFieldKeys
        : canAdministerWorkspace
          ? defaultKualiSourceDisplayFieldKeys(savedModules)
          : [],
    )
    setSchedule(feed?.schedule ?? 'manual')
    setLoadingConnection(true)
    void getKualiResearchConnection(api, orgId, vaultId)
      .then(setConnection)
      .catch(() => toast.error('Could not read the Kuali Research connection'))
      .finally(() => setLoadingConnection(false))
  }, [api, canAdministerWorkspace, feed, open, orgId, vaultId])

  const submit = async (): Promise<boolean> => {
    const options: KualiResearchFeedOptions = {
      apiUrl: apiUrl.trim(),
      modules,
      credentialSource,
    }
    const formError = kualiResearchFormError({
      apiUrl: options.apiUrl,
      modules,
      credentialSource,
      connection,
      apiKey,
    })
    if (formError && (canEditQuery || !feed)) {
      toast.error(formError)
      return false
    }
    setSaving(true)
    try {
      if (credentialSource === 'workspace' && apiKey) {
        await saveKualiResearchConnection(api, orgId, vaultId, apiKey)
        setConnection(current => ({ ...current, workspaceConfigured: true }))
        setApiKey('')
      }
      await (feed
        ? updateKualiResearchFeed(
            api,
            orgId,
            vaultId,
            feed.feedId,
            schedule,
            sourceDisplayFieldKeys,
            options,
          )
        : createKualiResearchFeed(api, orgId, vaultId, {
            initialSync: 'all',
            schedule,
            sourceDisplayFieldKeys,
            kualiResearch: options,
          }))
      // cm-coding: The feed mutation completes Save once the settings are
      // persisted. Connector cards, record catalogs, mappings, and dashboards
      // refresh independently; waiting for every read here can trap an
      // administrator in a saving dialog even though the requested change
      // succeeded and is safe to close.
      void onConfigured().catch(() => toast.error(
        'Kuali Research settings were saved, but connected views could not refresh',
      ))
      toast.success(
        editing
          ? 'Kuali Research settings saved.'
          : 'Kuali Research source connected.',
      )
      return true
    } catch (error: any) {
      const detail = error?.response?.data?.detail
        ?? error?.message
        ?? `Kuali Research ${editing ? 'update' : 'setup'} failed`
      toast.error(detail)
      return false
    } finally {
      setSaving(false)
    }
  }

  const deleteCredential = async () => {
    setSaving(true)
    try {
      await deleteKualiResearchConnection(api, orgId, vaultId)
      setConnection(current => ({ ...current, workspaceConfigured: false }))
      setApiKey('')
      toast.success('Kuali Research API key deleted')
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Could not delete the Kuali Research API key')
    } finally {
      setSaving(false)
    }
  }

  const updateModule = (module: KualiResearchModuleOptions) => {
    const next = modules.map(value => value.source === module.source ? module : value)
    setSourceDisplayFieldKeys(current => canAdministerWorkspace
      ? reconcileDisplayFieldKeys(current, modules, next)
      : current.filter(key => kualiSourceDisplayFields(next).some(field => field.key === key)))
    setModules(next)
  }
  const setSources = (sources: KualiResearchSourceType[]) => {
    const next = KUALI_RESEARCH_SOURCES
      .map(({ value }) => value)
      .filter(source => sources.includes(source))
      .map(source => modules.find(module => module.source === source) ?? newModule(source))
    setSourceDisplayFieldKeys(current => canAdministerWorkspace
      ? reconcileDisplayFieldKeys(current, modules, next)
      : current.filter(key => kualiSourceDisplayFields(next).some(field => field.key === key)))
    setModules(next)
  }

  return {
    editing,
    connection,
    loadingConnection,
    saving,
    apiKey,
    apiUrl,
    modules,
    sourceDisplayFields: kualiSourceDisplayFields(modules),
    sourceDisplayFieldKeys,
    sources: modules.map(module => module.source),
    schedule,
    credentialSource,
    setCredentialSource,
    setApiKey,
    setApiUrl,
    setSources,
    updateModule,
    setSourceDisplayFieldKeys: (value: string[]) => {
      setSourceDisplayFieldKeys(value)
    },
    setSchedule,
    submit,
    deleteCredential,
  }
}
